import React, { useEffect, useState } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Label } from '../ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'

const allowedUnits = [
  { value: 'kg', label: 'Kilograms' },
  { value: 'grams', label: 'Grams' },
  { value: 'liters', label: 'Liters' },
  { value: 'ml', label: 'Milliliters' },
  { value: 'pieces', label: 'Pieces' },
]

function AddMaterialForm({ onSuccess, selectedBranchId }: { onSuccess?: () => void; selectedBranchId?: string }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [suppliers, setSuppliers] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [unitType, setUnitType] = useState(allowedUnits[0].value)
  const [currentStock, setCurrentStock] = useState('')
  const [minThreshold, setMinThreshold] = useState('')
  const [costPerUnit, setCostPerUnit] = useState('')
  const [name, setName] = useState('')
  const [supplier, setSupplier] = useState('')

  useEffect(() => {
    if (user) {
      loadSuppliers();
    }
  }, [user])

  const loadSuppliers = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('raw_materials')
        .select('supplier')
        .eq('user_id', user?.id)
        .eq('branch_id', selectedBranchId)
        .not('supplier', 'is', null);

      if (error) {
        console.error('Error loading suppliers:', error);
        return;
      }

      if (data && Array.isArray(data)) {
        const unique = Array.from(new Set(data.map((m: { supplier?: string }) => m.supplier).filter(Boolean) as string[]))
        setSuppliers(unique)
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    setLoading(true);
    try {
      const values = {
        name,
        unit_type: unitType,
        current_stock: parseFloat(currentStock) || 0,
        min_threshold: parseFloat(minThreshold) || 0,
        cost_per_unit: parseFloat(costPerUnit) || 0,
        supplier: supplier || null,
        user_id: user.id,
        branch_id: selectedBranchId
      };

      const { error } = await (supabase as any)
        .from('raw_materials')
        .insert([values]);

      if (error) {
        throw error;
      }

      toast({ 
        title: 'Material added successfully', 
        description: `Added ${values.name} to inventory` 
      });
      // Reset form
      setName('');
      setUnitType(allowedUnits[0].value);
      setCurrentStock('');
      setMinThreshold('');
      setCostPerUnit('');
      setSupplier('');
      onSuccess?.();
    } catch (error) {
      console.error('Error adding material:', error);
      toast({
        title: 'Error adding material',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }

  // Real-time unit conversion (grams <-> kg, ml <-> liters)
  let converted = '';
  if (unitType === 'grams' && currentStock) converted = `${(parseFloat(currentStock) / 1000).toFixed(2)} kg`;
  if (unitType === 'kg' && currentStock) converted = `${(parseFloat(currentStock) * 1000).toFixed(0)} grams`;
  if (unitType === 'ml' && currentStock) converted = `${(parseFloat(currentStock) / 1000).toFixed(2)} liters`;
  if (unitType === 'liters' && currentStock) converted = `${(parseFloat(currentStock) * 1000).toFixed(0)} ml`;

  return (
    <form onSubmit={onSubmit} className="space-y-4 max-w-md mx-auto p-4 bg-white rounded shadow">
      <div>
        <Label htmlFor="name">Name</Label>
        <Input 
          id="name" 
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Beef, Lettuce, Tomatoes"
          required
        />
      </div>
      
      <div>
        <Label htmlFor="unit_type">Unit</Label>
        <Select value={unitType} onValueChange={setUnitType}>
          <SelectTrigger>
            <SelectValue placeholder="Select unit" />
          </SelectTrigger>
          <SelectContent>
            {allowedUnits.map(u => (
              <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label htmlFor="current_stock">Current Stock</Label>
        <Input 
          id="current_stock" 
          type="number" 
          step="any" 
          value={currentStock}
          onChange={(e) => setCurrentStock(e.target.value)}
          placeholder="0"
          required
        />
        {converted && <span className="text-xs text-gray-500">{converted}</span>}
      </div>
      
      <div>
        <Label htmlFor="min_threshold">Min Threshold</Label>
        <Input 
          id="min_threshold" 
          type="number" 
          step="any" 
          value={minThreshold}
          onChange={(e) => setMinThreshold(e.target.value)}
          placeholder="0"
          required
        />
      </div>
      
      <div>
        <Label htmlFor="cost_per_unit">Cost per Unit</Label>
        <Input 
          id="cost_per_unit" 
          type="number" 
          step="any" 
          value={costPerUnit}
          onChange={(e) => setCostPerUnit(e.target.value)}
          placeholder="0.00"
          required
        />
      </div>
      
      <div>
        <Label htmlFor="supplier">Supplier (Optional)</Label>
        <Input 
          id="supplier" 
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
          placeholder="Supplier name"
        />
      </div>
      
      <Button type="submit" disabled={loading} className="w-full">
        {loading ? 'Adding...' : 'Add Material'}
      </Button>
    </form>
  )
}

export { AddMaterialForm } 